import React, { Fragment } from 'react';
import type { ReactNode } from 'react';

import { partnerLogoMap as PARTNER_MAP } from 'wanderu-assets';
import { Transfer } from 'wanderu-component-itinerary';
import PartnerLogo from 'wanderu-component-partner-logo';
import { SearchResult } from 'wanderu-component-search-result';
import { i18nMoment as ourMoment, localize } from 'wanderu-i18n';

import { DEPART_DIRECTION } from '../../../constants';
import BookButton from '../../../containers/BookButton/BookButton';

import styles from './TripSummaryLine.scss';

import type { TFunction } from 'i18next';

// Types for the trip object
interface Trip {
    tripId: string;
    arriveId?: string;
    departId?: string;
    localizedArriveDateTime?: string;
    localizedDepartDateTime?: string;
}

interface Fare {
    description: Record<string, string>;
    price: number;
    type: string;
}

interface LiveCheckIds {
    affiliateId?: string;
    allTripIds?: string[];
    trackingId?: string;
}

type PartnerKey = 'booking' | 'kayak' | 'redbus';

interface TripSummaryLineProps {
    arrive: {
        cityName?: string;
    };
    arriveDate?: string;
    availableFares?: Fare[];
    carrierProviderGroup?: PartnerKey;
    className?: string;
    depart: {
        cityName?: string;
        localizedDateTime?: string;
    };
    destinationText?: string;
    direction?: 'depart' | 'return';
    forceModalClose?: boolean;
    i18nMoment: typeof ourMoment;
    liveCheckIds?: LiveCheckIds;
    liveChecksInProgress?: Record<string, boolean>;
    locale: string;
    onBookClick?: (isDeeplink: boolean, tripId: string, url?: string) => void;
    originText?: string;
    seats?: number;
    showIndividualBookButtons?: boolean;
    showReturnTripAdder?: boolean;
    showTotalPrice?: boolean;
    t: TFunction;
    trips: Trip[];
    wheelchairs?: number;
}

function hasTransferAfter(trips: Trip[], idx: number): boolean {
    return !!trips
        && idx < trips.length - 1
        && trips[idx].arriveId === trips[idx + 1].departId;
}

function hasSelfTransferAfter(trips: Trip[], idx: number): boolean {
    return !!trips
        && idx < trips.length - 1
        && trips[idx].arriveId !== trips[idx + 1].departId;
}

const TripSummaryLine: React.FC<TripSummaryLineProps> = ({
    arrive = {},
    availableFares,
    carrierProviderGroup,
    className,
    depart = {},
    direction = DEPART_DIRECTION,
    forceModalClose,
    i18nMoment,
    liveCheckIds = {},
    liveChecksInProgress = {},
    locale,
    onBookClick = () => undefined,
    seats,
    showIndividualBookButtons = false,
    showTotalPrice = false,
    t,
    trips = [],
    wheelchairs,
}) => {
    const renderTransferAfter = (idx: number): ReactNode => {
        const leg1 = trips[idx];
        const leg2 = trips[idx + 1];

        return (
            <Transfer
                arriveStationId={leg1?.arriveId}
                arriveTimestamp={leg1?.localizedArriveDateTime}
                departStationId={leg2?.departId}
                departTimestamp={leg2?.localizedDepartDateTime}
                type="transfer"
            />
        );
    };

    const renderSelfTransferAfter = (idx: number): ReactNode => {
        const leg1 = trips[idx];
        const leg2 = trips[idx + 1];

        return (
            <Transfer
                arriveStationId={leg2?.departId}
                arriveTimestamp={leg2?.localizedDepartDateTime}
                departStationId={leg1?.arriveId}
                departTimestamp={leg1?.localizedArriveDateTime}
                type="selfTransfer"
            />
        );
    };

    const renderItineraryForTrip = (trip: Trip, idx: number): ReactNode => {
        const { tripId } = trip;

        const busy = liveChecksInProgress[tripId];
        const lastItem = idx === trips.length - 1;

        return (
            <Fragment key={tripId}>
                <SearchResult
                    {...trip}
                    className={styles.searchResultContainer}
                    fareClasses={availableFares}
                    forceModalClose={forceModalClose}
                    liveCheckIds={liveCheckIds}
                    seats={seats}
                    showTotalPrice={showTotalPrice}
                    variant="tripSummary"
                    wheelchairs={wheelchairs}
                    disabled
                />

                {!!carrierProviderGroup && lastItem && (
                    <PartnerLogo
                        className={styles.partnerLogoMobile}
                        partner={
                            carrierProviderGroup
                                ? PARTNER_MAP[carrierProviderGroup]
                                : undefined
                        }
                        partnerId={carrierProviderGroup}
                        useFullSizeLogo={false}
                        flights
                    />
                )}

                {showIndividualBookButtons && (
                    <div className={styles.buttonContainer}>
                        <BookButton
                            className={styles.bookButton}
                            onClick={(
                                isDeeplink: boolean,
                                url?: string
                            ) => onBookClick?.(isDeeplink, tripId, url)}
                            seats={seats}
                            show={!busy}
                            tripIds={[tripId]}
                            tripItineraryId={tripId}
                            wheelchairs={wheelchairs}
                            deeplinkExists
                        />
                    </div>
                )}

                {hasTransferAfter(trips, idx) && renderTransferAfter(idx)}
                {hasSelfTransferAfter(trips, idx) && renderSelfTransferAfter(idx)}
            </Fragment>
        );
    };

    const { cityName: arriveCityName } = arrive || {};
    const { cityName: departCityName, localizedDateTime: departTime } = depart || {};

    const directionText = direction === DEPART_DIRECTION
        ? t('departure')
        : t('return');

    const departTimeFormatted = departTime
        ? i18nMoment.parseZone(departTime).shortDayAndWeekday(locale)
        : '';

    return (
        <div className={className}>
            <h2 className={styles.heading}>
                {directionText}
            </h2>
            <div className={styles.departArrive}>
                {departCityName} &#8250; {arriveCityName} &bull; {departTimeFormatted}
            </div>
            {trips.map(renderItineraryForTrip)}
        </div>
    );
};

export { TripSummaryLine };
export default localize(TripSummaryLine, ['i18nMoment', 't'], 'baseApp');
